import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { X } from 'lucide-react';
import { formatDate } from 'utils/date';
import { PromoCodeUsage } from 'types/promoCode';
import { useNavigate } from 'react-router-dom';
import { getDetailsModalStyles } from './PromoCodeDetailsModal.styles';

interface PromoCodeDetailsModalProps {
  open: boolean;
  onClose: () => void;
  promoCode: string;
  rows: PromoCodeUsage[];
  theme?: 'light' | 'dark';
}

const PromoCodeDetailsModal: React.FC<PromoCodeDetailsModalProps> = ({
  open,
  onClose,
  promoCode,
  rows,
  theme = 'dark',
}) => {
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const styles = getDetailsModalStyles(isDark);

  const columns: GridColDef[] = [
    {
      field: 'username',
      headerName: 'Username',
      flex: 1,
      renderCell: (params) => {
        const username = params.value || params.row.user || '-';
        const userId = params.row.userId;

        if (userId) {
          return (
            <Box
              onClick={() => {
                onClose();
                navigate(`/clients/${userId}`);
              }}
              sx={{
                color: '#00b3f5',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#0090c4',
                },
              }}
            >
              {username}
            </Box>
          );
        }
        return <Box>{username}</Box>;
      },
    },
    {
      field: 'usedAt',
      headerName: 'Used At',
      flex: 1,
      valueGetter: (value, row) => value || row.activatedDate || row.createdAt,
      valueFormatter: (value) => (value ? formatDate(value) : '-'),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: styles.paper,
      }}
    >
      <DialogTitle sx={styles.title}>
        Details for: {promoCode}
        <IconButton onClick={onClose} sx={{ color: isDark ? '#8698a6' : '#5d7182' }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, height: '400px' }}>
        <Box sx={{ height: '100%', width: '100%' }}>
          <DataGrid rows={rows} columns={columns} autoHeight={false} sx={styles.grid} />
        </Box>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ bgcolor: '#00b3f5', color: '#fff', '&:hover': { bgcolor: '#0090c4' } }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromoCodeDetailsModal;
